import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Screen, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CenteredSpinner, BackButton } from '~/commonComponents';
import { useAppSelector } from '~/hooks';
import { useViewedOfficeScope } from '~/hooks/useViewedOfficeScope';
import type { TViewedOfficeScope } from '~/types/ViewedOfficeScope';
import { FETCH_INVOICE_LIST, fetchInvoiceList } from '../../../actions';
import { createLoadingSelector } from '../../../api/selectors';
import { PaymentInvoiceTable } from './components/InvoiceTable';
import '../../../themes/global.scss';

const LIMIT_VALUE = 25;

/**
 * Invoices for the office the page was opened for: the selected office, one
 * location (`?officeId=`) or every location (`?scope=organization`).
 */
export default function InvoiceList() {
  const scope = useViewedOfficeScope();

  return scope ? <Invoices scope={scope} /> : <CenteredSpinner minHeight={400} />;
}

function Invoices({ scope }: { scope: TViewedOfficeScope }) {
  const dispatch = useDispatch();
  const myRef = useRef<HTMLDivElement>(null);

  const loadingSelector = createLoadingSelector([FETCH_INVOICE_LIST]);
  const isLoading = useAppSelector((state) => loadingSelector(state));

  const {
    invoices,
    isFinalPageInvoices,
    isActiveRequestInvoices,
    receiptReport,
    invoicesPagination,
    totalInvoices,
  } = useAppSelector((state) => state.job);

  const cvsDownload = useRef<{ link?: HTMLAnchorElement } | null>(null);

  useEffect(() => {
    dispatch(fetchInvoiceList({ page: 1, limit: LIMIT_VALUE, scope }));
  }, [dispatch, scope]);

  const handleScrollReceipts = () => {
    const userScrollHeight = window.innerHeight + window.scrollY + 1;
    const windowBottomHeight = document.documentElement.offsetHeight;

    if (userScrollHeight > windowBottomHeight && !isActiveRequestInvoices && !isFinalPageInvoices) {
      dispatch(fetchInvoiceList({ page: invoicesPagination?.page + 1, limit: LIMIT_VALUE, scope }));
    }
  };

  useEffect(() => {
    if (cvsDownload.current?.link && receiptReport !== '') {
      cvsDownload.current.link.click();
    }
  }, [receiptReport]);

  if (isLoading && invoices.length === 0) {
    return (
      <Screen variant="standard">
        <Screen.Header start={<BackButton />} title="Invoices" />
        <Screen.Body>
          <CenteredSpinner minHeight={400} />
        </Screen.Body>
      </Screen>
    );
  }

  return (
    <Screen variant="standard">
      <Screen.Header
        start={<BackButton />}
        title="Invoices"
        end={
          <Typography variant="body1" bold textAlign="right" color={colors.neutral[600]}>
            Total Invoices - {totalInvoices}
          </Typography>
        }
      />
      <Screen.Body>
        <PaymentInvoiceTable ref={myRef} onScroll={handleScrollReceipts} scope={scope} />
      </Screen.Body>
    </Screen>
  );
}
